import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { recoveryApi, ApiError } from '../services/recoveryApi';
import {
    SEIZED_ASSET_TYPE_OPTIONS,
    SeizedAssetStatus,
    SeizedAssetType,
    type CreateSeizedAssetPayload,
    type SeizedAsset,
    type SelectOption,
    type UpdateSeizedAssetPayload,
} from '../types/LegalCase';
import type { ErrorField } from '../types/ErrorField';

// Valor do formulário de criação de bem. Só os campos de identificação da família do
// assetType escolhido vêm preenchidos; o hook limpa os vazios antes de enviar.
interface AddSeizedAssetValue extends CreateSeizedAssetPayload {}

// Gerencia os bens penhorados de um caso: lista, adiciona, edita campos, muda status no
// ciclo da penhora e remove. Fetch próprio pelo id do caso (a seção atualiza sem recarregar
// o page-view inteiro). Mesmo padrão do useLegalCaseMeasures (seededCaseRef re-seed por
// caso, opções de tipo vindas do page-form com fallback local).
export default function useLegalCaseSeizedAssets(caseId: string | null, initialAssets?: SeizedAsset[]) {
    const [assets, setAssets] = useState<SeizedAsset[]>(initialAssets || []);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [errorFields, setErrorFields] = useState<ErrorField[]>([]);
    const [typeOptions, setTypeOptions] = useState<SelectOption[]>(SEIZED_ASSET_TYPE_OPTIONS);
    const seededCaseRef = useRef<string | null>(null);

    const fetchAssets = useCallback(async () => {
        if (!caseId) return;
        setIsLoading(true);
        try {
            const result = await recoveryApi.getSeizedAssets(caseId);
            setAssets(result.items || []);
        } catch {
            // silencioso — a seção já mostra os bens que vieram no page-view
        } finally {
            setIsLoading(false);
        }
    }, [caseId]);

    useEffect(() => {
        if (seededCaseRef.current === caseId) return;
        seededCaseRef.current = caseId;
        setAssets(initialAssets || []);
        setErrorFields([]);
        // initialAssets fora das deps de propósito (referência nova a cada render).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [caseId]);

    useEffect(() => {
        const loadTypes = async () => {
            try {
                const opts = await recoveryApi.getLegalCaseFormOptions();
                if (opts.seizedAssetTypes?.length) setTypeOptions(opts.seizedAssetTypes);
            } catch {
                // fallback local já preenche
            }
        };
        loadTypes();
    }, []);

    const clearErrors = () => setErrorFields([]);

    const addAsset = async (data: AddSeizedAssetValue): Promise<boolean> => {
        if (!caseId) return false;
        setIsSaving(true);
        setErrorFields([]);
        try {
            await recoveryApi.addSeizedAsset(caseId, {
                ...data,
                status: data.status || SeizedAssetStatus.LOCATED,
            });
            await fetchAssets();
            toast.success('Bem registrado');
            return true;
        } catch (e: any) {
            if (e instanceof ApiError && e.status === 422 && e.data?.errors) {
                toast.error(e.data.message || 'Dados inválidos. Verifique os campos.');
                setErrorFields(e.data.errors);
                return false;
            }
            if (e instanceof ApiError && e.status === 404) {
                toast.error(e.data?.message || 'Caso não encontrado');
                return false;
            }
            toast.error(e?.message || 'Erro ao registrar bem');
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    const updateAsset = async (assetId: string, data: UpdateSeizedAssetPayload): Promise<boolean> => {
        if (!caseId) return false;
        setIsSaving(true);
        setErrorFields([]);
        try {
            await recoveryApi.updateSeizedAsset(caseId, assetId, data);
            await fetchAssets();
            toast.success('Bem atualizado');
            return true;
        } catch (e: any) {
            if (e instanceof ApiError && e.status === 422 && e.data?.errors) {
                toast.error(e.data.message || 'Dados inválidos. Verifique os campos.');
                setErrorFields(e.data.errors);
                return false;
            }
            toast.error(e?.data?.message || e?.message || 'Erro ao atualizar bem');
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    const changeStatus = async (assetId: string, status: SeizedAssetStatus) => {
        if (!caseId) return;
        setIsSaving(true);
        try {
            await recoveryApi.updateSeizedAssetStatus(caseId, assetId, status);
            await fetchAssets();
            toast.success('Status atualizado');
        } catch (e: any) {
            // 422 = transição inválida; o backend explica no message.
            toast.error(e?.data?.message || e?.message || 'Erro ao atualizar status');
        } finally {
            setIsSaving(false);
        }
    };

    const removeAsset = async (assetId: string) => {
        if (!caseId) return;
        setIsSaving(true);
        try {
            await recoveryApi.removeSeizedAsset(caseId, assetId);
            await fetchAssets();
            toast.success('Bem removido');
        } catch (e: any) {
            toast.error(e?.data?.message || e?.message || 'Erro ao remover bem');
        } finally {
            setIsSaving(false);
        }
    };

    return {
        assets, isLoading, isSaving, errorFields, typeOptions,
        addAsset, updateAsset, changeStatus, removeAsset, clearErrors,
    };
}

export type { AddSeizedAssetValue };
export { SeizedAssetType };
